import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "@tsp-core/store/utils";

import {
  BrandUuid,
  BrandsDictionary,
  BrandsDictionaryRecord,
  LocaleCode,
  LocalesDictionary,
  LocalesDictionaryRecord,
  ProjectsPortfolio,
  PropertiesDictionary,
  PropertiesDictionaryRecord,
  PropertyUuid,
} from "../types";

type GetProjectsPortfolioResponseData = {
  projectsPortfolio: {
    [localeCode: LocaleCode]: {
      name: string;
      brands: {
        [brandUuid: BrandUuid]: {
          name: string;
          label: string;
          properties: {
            [propertyUuid: PropertyUuid]: {
              name: string;
              label: string;
              isActive: boolean;
              platformName: string;
            };
          };
        };
      };
    };
  };
};

export const projectsPortfolioApi = createApi({
  reducerPath: "@projects-portfolio/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  endpoints: (builder) => ({
    getProjectsPortfolio: builder.query<ProjectsPortfolio, void>({
      query: () => ({
        url: "/settings/getProjectsPortfolio",
        method: "GET",
      }),
      transformResponse: (response: GetProjectsPortfolioResponseData): ProjectsPortfolio => {
        const locales: LocalesDictionary = {};
        const brands: BrandsDictionary = {};
        const properties: PropertiesDictionary = {};

        Object.entries(response.projectsPortfolio).forEach(([localeCode, rawLocale]) => {
          const locale: LocalesDictionaryRecord = {
            code: localeCode as LocaleCode,
            name: rawLocale.name,
            brandsUuids: [],
            propertiesUuids: [],
          };

          Object.entries(rawLocale.brands).forEach(([brandUuid, rawBrand]) => {
            const brand: BrandsDictionaryRecord = {
              uuid: brandUuid as BrandUuid,
              name: rawBrand.name,
              label: rawBrand.label,
              localeCode: localeCode as LocaleCode,
              propertiesUuids: [],
            };

            Object.entries(rawBrand.properties).forEach(([propertyUuid, rawProperty]) => {
              const property: PropertiesDictionaryRecord = {
                uuid: propertyUuid as PropertyUuid,
                name: rawProperty.name,
                label: rawProperty.label,
                isActive: rawProperty.isActive,
                localeCode: localeCode as LocaleCode,
                brandUuid: brandUuid as BrandUuid,
                platformName: rawProperty.platformName,
              };

              properties[property.uuid] = property;

              locale.propertiesUuids.push(propertyUuid as PropertyUuid);
              brand.propertiesUuids.push(propertyUuid as PropertyUuid);
            });

            locale.brandsUuids.push(brandUuid as BrandUuid);
            brands[brandUuid as BrandUuid] = brand;
          });

          locales[localeCode as LocaleCode] = locale;
        });

        return {
          locales,
          brands,
          properties,
        };
      },
    }),
  }),
});
